// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import type { AlgoId, DitherParams, PaletteId, Preset } from './types';

export const DEFAULT_PRESET_ID = 'onebit';

const base: DitherParams = {
  palette: 'mono',
  colors: 4,
  algorithm: 'floyd',
  pixelSize: 1,
  brightness: 0,
  contrast: 0,
  serpentine: true,
};

export const PRESETS: Preset[] = [
  {
    id: 'onebit',
    label: '1-bit',
    group: 'Mono',
    hint: 'Classic black & white Floyd–Steinberg — zines, e-ink, thermal printers.',
    params: { ...base, palette: 'mono', algorithm: 'floyd', pixelSize: 1 },
  },
  {
    id: 'atkinson',
    label: 'Atkinson',
    group: 'Mono',
    hint: 'The crisp, high-contrast early-Macintosh / HyperCard look.',
    params: { ...base, palette: 'mono', algorithm: 'atkinson', pixelSize: 1 },
  },
  {
    id: 'ordered',
    label: 'Ordered',
    group: 'Mono',
    hint: 'Regular 8×8 Bayer cross-hatch — a newsprint / halftone feel.',
    params: { ...base, palette: 'mono', algorithm: 'bayer8', pixelSize: 1 },
  },
  {
    id: 'grayscale',
    label: 'Grayscale',
    group: 'Mono',
    hint: 'Four grey levels, Floyd–Steinberg. Smooth mono gradients.',
    params: { ...base, palette: 'gray', colors: 4, algorithm: 'floyd', pixelSize: 1 },
  },
  {
    id: 'gameboy',
    label: 'Game Boy',
    group: 'Colour',
    hint: 'The four-green DMG palette, chunky pixels — the Game Boy Camera vibe.',
    params: { ...base, palette: 'gameboy', algorithm: 'floyd', pixelSize: 3 },
  },
  {
    id: 'cga',
    label: 'CGA',
    group: 'Colour',
    hint: 'Cyan / magenta / white on black — 1980s PC graphics.',
    params: { ...base, palette: 'cga', algorithm: 'floyd', pixelSize: 2 },
  },
  {
    id: 'retro16',
    label: 'Retro 16',
    group: 'Colour',
    hint: '16 colours picked from your image with dithering — a clean retro poster.',
    params: { ...base, palette: 'adaptive', colors: 16, algorithm: 'floyd', pixelSize: 2 },
  },
  {
    id: 'poster8',
    label: 'Poster 8',
    group: 'Colour',
    hint: '8 image colours, Atkinson, chunky — a bold silk-screen look.',
    params: { ...base, palette: 'adaptive', colors: 8, algorithm: 'atkinson', pixelSize: 3 },
  },
];

export function paramsFromPreset(id: string): DitherParams {
  const p = PRESETS.find((x) => x.id === id) ?? PRESETS[0];
  return { ...p.params };
}

/** Return the id of the preset matching `params` exactly, or null (→ "Custom"). */
export function matchPreset(params: DitherParams): string | null {
  for (const p of PRESETS) {
    const q = p.params;
    if (
      q.palette === params.palette &&
      q.algorithm === params.algorithm &&
      q.pixelSize === params.pixelSize &&
      q.brightness === params.brightness &&
      q.contrast === params.contrast &&
      q.serpentine === params.serpentine &&
      // colours only matters for the palettes that use it
      (usesColorCount(q.palette) ? q.colors === params.colors : true)
    ) {
      return p.id;
    }
  }
  return null;
}

/** Whether the palette's colour-count control is meaningful. */
export function usesColorCount(palette: PaletteId): boolean {
  return palette === 'gray' || palette === 'sepia' || palette === 'adaptive';
}

/** Min/max colour count for a palette (fixed palettes report their own size). */
export function colorRange(palette: PaletteId): { min: number; max: number } {
  if (palette === 'adaptive') return { min: 2, max: 32 };
  if (palette === 'gray' || palette === 'sepia') return { min: 2, max: 8 };
  return { min: 2, max: 8 };
}

export const ALGO_LABELS: Record<AlgoId, string> = {
  floyd: 'Floyd–Steinberg',
  atkinson: 'Atkinson',
  jjn: 'Jarvis–Judice–Ninke',
  stucki: 'Stucki',
  bayer4: 'Ordered 4×4',
  bayer8: 'Ordered 8×8',
  none: 'Threshold (none)',
};

export const PALETTE_LABELS: Record<PaletteId, string> = {
  mono: '1-bit mono',
  gray: 'Grayscale',
  gameboy: 'Game Boy',
  cga: 'CGA',
  sepia: 'Sepia',
  adaptive: 'Adaptive (from image)',
};

/** True for the error-diffusion algorithms where serpentine scanning applies. */
export function isDiffusion(algo: AlgoId): boolean {
  return algo === 'floyd' || algo === 'atkinson' || algo === 'jjn' || algo === 'stucki';
}
